/**
 * The craft half of ad-copy generation — what makes copy GOOD, as distinct
 * from `ad-contract.ts`, which is what makes copy SHIPPABLE.
 *
 * WHY THIS EXISTS. `generate-ad-copy.ts` shipped with ~10 structural rules
 * ("exactly 2 primary texts", "no em dashes") and nothing about persuasion, so
 * every account got the same shaped, evidence-safe, forgettable ad. Meanwhile
 * `loadGrounding` was already reading — and the prompt was already discarding —
 * the brand's tone, pillars, avoid-phrases, competitors, its saved audience
 * personas (who / desire / pain language / objection) and its ranked angle
 * bank. The copy was generic because the brand kit never reached the model,
 * not because the model was weak.
 *
 * PROVENANCE OF THE CRAFT RULES. Distilled from the reverse-engineered
 * incumbent skills in `docs/research/competitors/marketer-com/skills/`,
 * primarily `creative-copy.md` (their master copywriter system prompt) and
 * `ad-copy-meta.md` (their constraints card), cross-checked against LinkedIn's
 * own published guidance for the LinkedIn block. These are craft heuristics
 * from a competitor's prompt corpus, NOT platform policy and NOT verified
 * numbers — every hard character limit still comes from `AD_SPECS`, whose
 * provenance strings say which are platform-doc and which are they-assert.
 * Nothing here may become citable evidence: these blocks shape HOW the copy is
 * written, never WHAT it is allowed to claim. `filterVerifiableEvidence` is
 * still the only gate on that, and none of these strings are fed to it.
 */

import { AD_SPECS } from '@/constants/ad-specs';
import type { AngleGround, AudienceGround, BrandGround } from '@/lib/briefs/grounding';

/**
 * THE COUNTS THE BRIEF IS WRITTEN AGAINST, IN ONE PLACE.
 *
 * The brief used to state "30 characters or fewer" as a literal, and a test
 * asserted that literal appeared — which asserts only that the brief agrees
 * with itself. Change `AD_SPECS.meta.description.adviseMax` to 27 and the
 * brief kept saying 30, silently, with the test still green. A title
 * claiming consistency is not a guard (same class as this repo's comment
 * rule), so the number the model is given is now DERIVED from the same spec
 * the readiness gate measures against, and the prompt's structural rules
 * read from here too.
 *
 * The word counts have no `AD_SPECS` entry to derive from — Meta publishes
 * no word limit — so they live here as the single definition that both the
 * brief and the Meta structural rules interpolate.
 */
export const COPY_BRIEF_LIMITS = {
  hookMaxWords: 12,
  punchyMaxWords: 25,
  fullerMinWords: 35,
  primaryTextMaxWords: 60,
  headlineMaxWords: 6,
  descriptionMaxChars: AD_SPECS.meta.description.adviseMax ?? 30,
  hookFoldMaxChars: AD_SPECS.meta.hookFold.adviseMax ?? 125,
} as const;

/**
 * The rules that decide whether copy persuades, on any platform. Ordered so
 * the reader-model hits the Rule of 1 before it hits anything it could stack.
 */
export const SHARED_CRAFT_RULES: string[] = [
  'Rule of 1: one audience, one desire, one message per ad. Two value propositions is zero value propositions — if a second promise is worth making, it is a different ad.',
  'Write to ONE awareness stage and stay there. Unaware needs a surprising fact; problem-aware needs the problem named in their own words; solution-aware needs your mechanism to differentiate you; product-aware needs proof and objection removal; most-aware needs only the offer. Mixing stages is the single most common reason copy feels off.',
  'Every claim needs a reason the reader can picture: how it works and what they get. Say it the way a person says it out loud. "Because" is one connector among many ("so", "since", "with", "by"), and a sentence built as [instruction] because [fact] reads as machine-written.',
  'Specificity is proof. Numbers, timeframes, and named outcomes ("in 14 days", "cut invoice prep from 3 hours to 20 minutes") persuade; vague superlatives ("premium", "world-class", "seamless") are read as filler and skipped.',
  "Use the audience's own words, not the brand's words. Language lifted from real customers outperforms language invented for them.",
  'Emotion opens, logic supports, emotion closes. The hook is felt, the body is reasoned, the last line returns to the feeling.',
  'Could this copy ONLY be about this product for this audience? If it could be pasted onto a competitor unchanged, it has said nothing — rewrite it.',
];

/**
 * Hook patterns are given as a MENU with an explicit do-not-stack rule.
 * Listing them without that rule produces copy that opens with a question,
 * then a statistic, then an identity call-out — three hooks and no through
 * line, which is exactly what "AI-written ad" looks like.
 */
export const HOOK_FRAMEWORKS: string[] = [
  'Choose exactly ONE hook framework per ad and commit to it. Do not stack them.',
  '- Problem → agitate → solve: for pain-led angles. Name the problem, make the cost of it concrete, then resolve.',
  '- Before → after → bridge: for transformation angles. Current state, desired state, the product as the path between.',
  '- Proof lead: open on the single strongest specific number or real quote. Only when the proof is genuinely the strongest asset on file.',
  '- Identity call-out: name the reader in line one, as a statement they recognise about themselves ("You run payroll for a team of eighteen off one spreadsheet."), never as a yes/no question — a question is answered "no" for free, which the hook anti-patterns below reject. Doubles as a self-selection signal.',
  '- Objection flip: open on the reason they would say no, then answer it. Strongest for warm and returning readers.',
  '- Contrarian: challenge an assumption the audience holds, then deliver the genuine insight behind it. Requires a real insight; without one it is just provocation.',
];

/**
 * Machine-readable ids for the six substantive `HOOK_FRAMEWORKS` entries
 * above (the menu's first line — "choose exactly ONE... do not stack" — is
 * an instruction, not a framework, so it has no id here). Exists so code can
 * ASSIGN a framework per candidate rather than leave the choice to whichever
 * one the model reaches for by default — the fix for finding (5), 2026-09-03:
 * three candidates that were three rerolls of the same angle even though
 * seven frameworks were on the menu.
 */
export const HOOK_FRAMEWORK_IDS = [
  'problem_agitate_solve',
  'before_after_bridge',
  'proof_lead',
  'identity_callout',
  'objection_flip',
  'contrarian',
] as const;
export type HookFrameworkId = (typeof HOOK_FRAMEWORK_IDS)[number];

export const HOOK_FRAMEWORK_LABELS: Record<HookFrameworkId, string> = {
  problem_agitate_solve: 'Problem → agitate → solve',
  before_after_bridge: 'Before → after → bridge',
  proof_lead: 'Proof lead',
  identity_callout: 'Identity call-out',
  objection_flip: 'Objection flip',
  contrarian: 'Contrarian',
};

/**
 * The full how-to-write-it text for each `HOOK_FRAMEWORK_IDS` entry, derived
 * from `HOOK_FRAMEWORKS` by position (index 0 there is the menu's do-not-
 * stack instruction, not a framework, so every id maps to `HOOK_FRAMEWORKS[i
 * + 1]`) rather than duplicated by hand — one source of text, so the menu a
 * model sees when choosing freely and the single line it sees when a
 * framework is ASSIGNED (`generate-grounded-ad-copy.ts`'s `frameworkIds`,
 * the fix for finding (5), 2026-09-03) can never drift apart.
 */
export const HOOK_FRAMEWORK_DESCRIPTIONS: Record<HookFrameworkId, string> = Object.fromEntries(
  HOOK_FRAMEWORK_IDS.map((id, index) => [id, HOOK_FRAMEWORKS[index + 1].replace(/^- /, '')]),
) as Record<HookFrameworkId, string>;

/**
 * The default three frameworks a Meta copy suggestion routes its three
 * candidates through (`copy-suggestions-core.ts`) — one pain-led, one
 * transformation-led, one identity-led, so the three candidates a marketer
 * sees are three genuinely different angles rather than three rerolls of
 * whichever one framework the model reached for by default (the incident
 * `HOOK_FRAMEWORK_IDS` documents). Fixed rather than randomized: a stable
 * default is easier to reason about and to test than a shuffled one, and
 * nothing here claims these three are the ONLY good spread — just a
 * deliberately distinct one.
 */
export const DEFAULT_META_CANDIDATE_FRAMEWORKS: readonly [
  HookFrameworkId,
  HookFrameworkId,
  HookFrameworkId,
] = ['problem_agitate_solve', 'before_after_bridge', 'identity_callout'];

export type MetaHeadlineRole = 'pain_restatement' | 'outcome';

/**
 * Which headline role should ship FIRST (`headlines[0]`, the one
 * `copy-suggestions-core.ts` reads as THE headline — `ad.headlines[0]`) for a
 * given assigned hook framework. Before 2026-09-03 this was not a decision at
 * all: `MetaAdCopyOutputSchema`'s tuple pinned `headlines[0]` to the literal
 * `'pain_restatement'` unconditionally, and `normalizeMetaAdCopy` reordered
 * every draft to match regardless of what the model wrote. The judge called
 * that wasted real estate for cold prospecting in 6 of 7 runs: a hook already
 * built on `before_after_bridge` or `identity_callout` has already resolved
 * the pain by the time the reader reaches the headline, so restating it there
 * is the headline's one slot spent on ground the primary text already
 * covered. `problem_agitate_solve` keeps pain first because that framework's
 * hook has not yet turned to the solution — the headline is still doing the
 * agitation's job. Every other framework (`proof_lead`, `objection_flip`,
 * `contrarian`) defaults to the pre-existing pain-first order: none of them
 * carries the same "already resolved" argument the other two do, and this
 * function's job is only to say where the evidence is that order is wrong,
 * not to guess at frameworks nobody has judged yet.
 *
 * `frameworkId` is `undefined` when the model chose its own framework
 * (`frameworkIds` not supplied to `generateGroundedAdCopy`) — pre-existing,
 * unchanged pain-first behavior in that case.
 */
export function leadHeadlineRoleForFramework(
  frameworkId: HookFrameworkId | undefined,
): MetaHeadlineRole {
  if (frameworkId === 'before_after_bridge' || frameworkId === 'identity_callout') {
    return 'outcome';
  }
  return 'pain_restatement';
}

/** The openings that mark copy as generated rather than written. */
export const HOOK_ANTI_PATTERNS: string[] = [
  'Never open with a feature. Features belong in the body, after the reader has a reason to care.',
  'Never open with the brand or with "we". The reader has never heard of the brand and is not interested in it yet.',
  'Never open with floaty rhetorical questions ("Tired of feeling tired?", "Want to grow your business?"). They are answerable with "no" and cost nothing to scroll past.',
  'Never open on aspiration ("Live your best life"). Aspiration is the destination, not the entry point.',
  'No hype, no engagement bait, no emoji walls, no ALL-CAPS, no exclamation stacking.',
];

/**
 * ANTI-CORNY — countable failure modes the craft rules above don't stop on
 * their own.
 *
 * PROVENANCE OF THE INCIDENT. On 2026-09-03 an agent hand-wrote the primary
 * text Mora's own Meta ad shipped with, graded it against `ad-readiness.ts`
 * itself, and sent it to Meta twice: "Mora reads your website first —
 * archetype, voice, palette, pillars — so the first draft already sounds
 * like you. [...] look at the brand kit it builds from your site and judge
 * the drafts yourself." The founder called it corny. It is not one failure —
 * it is four countable ones stacked in a single paragraph: an em-dash
 * bracketed list standing in for a hook, the product narrating its own
 * internal steps instead of naming what the reader gets, a 30+ character
 * description doing real work, and a claim ("so the first draft already
 * sounds like you") with no stated mechanism.
 *
 * Every rule below is deliberately CHECKABLE, not just followable — see
 * `checkAdReadiness` (`ad-readiness.ts`) and `evaluateAdCopyAntiCorny`
 * (`copy-assistant.ts`), which enforce these as code the model cannot drift
 * past by sounding confident. Only the em-dash rule is a hard house style
 * rule (`slop.ts`'s `emDashBudget: 0`, already enforced elsewhere as a
 * block); the rest stay warnings, per this repo's standing rule that only
 * genuinely objective, assembly-breaking defects may fail a launch — taste
 * is scored, never blocked.
 */
export const ANTI_CORNY_RULES: string[] = [
  'Never narrate the product\'s own process to the reader ("we read your website...", "it analyzes your store...", "so the first draft already..."). The reader wants what THEY get, not a tour of how the machine works.',
  'No em dash, ever, in ad copy. An em-dash-bracketed list standing in for the hook ("X — a, b, c — so Y") is the single most common tell of copy written to sound impressive rather than to be read.',
  `The hook — the first sentence of the primary text — must stand alone in ~${COPY_BRIEF_LIMITS.hookFoldMaxChars} characters or fewer and must name one concrete outcome or tension for the reader, not a description of the product's internal steps.`,
  'Every claim needs its reason close by, in the same sentence or the next one, phrased as natural speech. A claim followed by an unrelated sentence is not a reason, it is two thoughts that happen to be adjacent. Never write [instruction] because [fact], such as "Exit cleanly because seven CFPs plan for 18 months". Write "Seven CFPs spend an average of 18 months getting the sale ready".',
  `Meta descriptions render at roughly ${COPY_BRIEF_LIMITS.descriptionMaxChars} characters before they are cut. Keep the description that short and non-load-bearing — a longer description is not "more information", it is copy nobody will read.`,
  'Match the CTA to how cold the audience is. A prospecting or Advantage+ audience gets a low-commitment CTA (Learn More, Watch More, Contact Us) — never Shop Now, Sign Up, or Get Offer on a reader who has never heard of the brand.',
  'Never trail off on an ellipsis or an unfinished clause. Land the sentence — a trailing "..." is a rhetorical flourish standing in for a thought that was not finished.',
];

function truncateList(values: string[], limit: number): string[] {
  return values.filter((value) => value.trim()).slice(0, limit);
}

/**
 * The brand kit as STYLE AND CONSTRAINT — deliberately not as evidence.
 *
 * Tone, pillars and forbidden phrases govern how the copy sounds; they are not
 * facts about the product and must never be citable. The block says so
 * explicitly, because the same model is simultaneously being told to cite only
 * verbatim evidence, and an unlabelled brand block is exactly the kind of
 * authoritative-looking text a model will reach for when it needs a source.
 */
export function buildBrandVoiceBlock(brand: BrandGround): string[] {
  const lines: string[] = [];
  const facts: string[] = [];

  if (brand.brandName) facts.push(`Brand name: ${brand.brandName}`);
  if (brand.industry) facts.push(`Industry: ${brand.industry}`);
  if (brand.businessType) facts.push(`Business type: ${brand.businessType}`);
  if (brand.tone) facts.push(`Brand tone of voice: ${brand.tone}`);
  if (brand.instructions) facts.push(`Binding workspace instructions: ${brand.instructions}`);
  if (brand.audienceDescription) facts.push(`Who the brand sells to: ${brand.audienceDescription}`);
  const pillars = truncateList(brand.pillars, 6);
  if (pillars.length) facts.push(`Content pillars: ${pillars.join(', ')}`);
  const competitors = truncateList(brand.competitors, 6);
  if (competitors.length) {
    facts.push(
      `Named competitors: ${competitors.join(', ')} — never name them in the copy unless the user asked for a comparison.`,
    );
  }

  if (facts.length === 0) return lines;

  lines.push('BRAND CONTEXT — how this must SOUND. This is style, not evidence:');
  for (const fact of facts) lines.push(`- ${fact}`);
  lines.push(
    'Match this tone. None of the lines in this block may be quoted as evidence, and the brand name may only appear if it is genuinely useful to the reader.',
  );

  const avoid = truncateList(brand.avoidPhrases, 20);
  if (avoid.length) {
    lines.push(
      `Words and phrases this brand forbids — do not use any of them in any field: ${avoid.join(', ')}.`,
    );
  }
  const approved = truncateList(brand.approvedPhrases ?? [], 20);
  if (approved.length) {
    lines.push(
      `Approved house phrases — use only when they fit naturally and never treat them as evidence: ${approved.join(', ')}.`,
    );
  }

  return lines;
}

/**
 * Saved audience rows are the closest thing the account has to a voice-of-
 * customer bank. `painLanguage` and `objection` are the two fields that
 * actually change the copy, so they lead. They are still not evidence: a
 * persona describes who is being written TO, which is a targeting decision, not
 * a verifiable claim about the product.
 */
export function buildAudienceBlock(audiences: AudienceGround[]): string[] {
  const usable = audiences.filter((audience) => audience.who?.trim() || audience.desire?.trim());
  if (usable.length === 0) {
    return [
      'NO saved audience is on file. Write to the single most plausible buyer implied by the product and brand context, and keep the reader description concrete rather than inventing demographics or a persona name.',
    ];
  }

  const lines: string[] = [
    'AUDIENCE — write to exactly ONE of these, the best fit for this product. Targeting context, not evidence:',
  ];
  for (const audience of usable.slice(0, 4)) {
    const parts = [`${audience.name}: ${audience.who}`];
    if (audience.desire) parts.push(`wants: ${audience.desire}`);
    if (audience.painLanguage) parts.push(`says it like this: "${audience.painLanguage}"`);
    if (audience.objection) parts.push(`objects that: ${audience.objection}`);
    lines.push(`- ${parts.join(' · ')}`);
  }
  lines.push(
    'Mirror their phrasing and answer their objection head-on. Their recorded wording shows you the register to write in; it is not a source you may cite as evidence.',
  );
  return lines;
}

/**
 * The angle bank is the account's own record of what it has decided to say.
 * Reusing a ranked angle keeps a Brief's ads coherent with the rest of its
 * content instead of re-deriving a position per ad.
 */
export function buildAngleBlock(angles: AngleGround[]): string[] {
  const ranked = angles
    .filter((angle) => angle.statement?.trim())
    .sort((a, b) => (b.rankScore ?? 0) - (a.rankScore ?? 0))
    .slice(0, 5);
  if (ranked.length === 0) return [];

  return [
    "ANGLE BANK — positions this account has already committed to, best-ranked first. Build on the one that fits this product rather than inventing a new position; if none fit, say nothing about it and write the angle the product's own evidence supports:",
    ...ranked.map((angle) => `- [${angle.lane}] ${angle.statement}`),
  ];
}

/**
 * Meta-specific craft. The length matrix is the operative part: "how long
 * should it be" is the question the model otherwise answers by defaulting to
 * medium-length copy for every product, which is wrong for both a $28 necklace
 * and a supplement sold to 60-year-olds.
 */
export const META_CRAFT_RULES: string[] = [
  'First, classify the product silently and let it drive the copy: PRODUCT-SOLVING (fixes a problem — supplements, skincare, tools, software, services) needs mechanism and proof; DESIRE-DRIVEN (bought for identity, aesthetics, or feeling — fashion, jewellery, decor) needs identity and occasion, and should be short because the image is doing the selling.',
  'Length follows the product and reader, under a hard ceiling: for cold prospecting the whole primary text is 60 words or fewer, whatever the product. Inside that ceiling, desire-driven runs 1–3 sentences, product-solving 2–4 for a younger reader and up to 4–8 for an older one who genuinely reads. Only a warm or returning reader who already knows the brand may run past 60 words. When unsure, shorter wins — never pad.',
  'The first line IS the ad. Draft at least five hooks internally, throw away every one that is an adjective rather than a thing, and keep the single one a skimmer could not scroll past.',
  'The headline is the CTA-side unit, not a summary — it must NOT restate the first line of the primary text. Give it the offer or the outcome, and let it stand alone.',
  'The description often does not render at all. Use it for a bonus — an offer, a guarantee, free shipping, one proof point — and never put anything load-bearing in it.',
  'Match the CTA to how warm the reader is. Cold readers get low-commitment framing (learn, see, try). Returning readers get the direct ask. Never push a hard purchase CTA at someone meeting the brand for the first time.',
  'Copy must be congruent with the image it will run beside and the page it lands on — same promise, same offer, same words. Copy that contradicts its creative fails even when it reads well.',
];

/**
 * Google RSA craft.
 *
 * The governing fact, and the one that makes RSA copy unlike every other
 * placement: an RSA is not a written ad, it is an INVENTORY. Google assembles a
 * different combination of headlines and descriptions for every auction, so
 * each asset has to survive being shown beside any two others. Copy written as
 * a sentence split across three headlines becomes garbage the moment the
 * machine picks only one of them.
 */
export const GOOGLE_RSA_CRAFT_RULES: string[] = [
  'You are writing an INVENTORY of interchangeable assets, not an ad. Google mixes any headline with any two others, in any order, for every auction. Every headline must therefore be self-contained and read correctly next to any other pair.',
  'Never split a sentence across headlines. "Get The Best" plus "Rings Delivered Today" reads as nonsense the moment Google shows only one of them.',
  'Fifteen rephrasings of one benefit is a failed set — it gives the optimizer nothing to learn from. Real variety comes from covering different JOBS: mirroring the search query, naming the outcome, giving a specific proof, stating the offer, and calling to action.',
  'Descriptions are self-contained too. Any two of them may show, in either order, so each must stand alone rather than continuing the one before it.',
  'Mirror the language a person would actually type to find this. The winning RSA reflects the searcher’s own words back at them — but only where they fit naturally; a headline stuffed with keywords reads as spam and depresses clicks.',
  'Display paths are decoration for the visible URL, not keyword space. Keep them short, readable, and human.',
  'Character limits here are rejections, not suggestions: Google refuses any headline over 30 characters, any description over 90, any path over 15. Count the characters — do not estimate them.',
  'Regulated categories (health, finance, legal) restrict superlatives and outcome promises. Neutral, factual framing gets served; an aggressive claim gets disapproved and serves nothing.',
];

/**
 * THE REVIEW RUBRIC.
 *
 * Adapted from the incumbent's own creative-labeling scoring bands, which exist
 * to grade finished ads after the fact. Running the same rubric BEFORE the copy
 * is saved is the cheap half of that idea: the model that wrote the copy is a
 * poor judge of it while it is still writing, but a good one when handed the
 * finished text and an explicit bar.
 *
 * The anchored bands matter more than the number. An unanchored 1–10 request
 * makes a model answer 7 to almost anything, which is worse than not scoring at
 * all because it manufactures the appearance of a quality gate.
 */
export const HOOK_SCORE_BANDS: string[] = [
  '1–3 — There is no hook. It opens on the brand, on a feature, or on a generic question.',
  '4–6 — Competent but generic. A direct competitor could run this line unchanged.',
  '7–8 — Strong. Specific to this product and this reader, and it earns the next line.',
  '9–10 — Genuinely scroll-stopping. Specific, surprising, and impossible to mistake for another advertiser.',
];

/** The five things a hook can be good AT, used to make the score explainable. */
export const HOOK_QUALITY_CRITERIA: string[] = [
  'Clarity — is it instantly understood, with no re-reading?',
  'Relevance — does it name a problem or situation this reader actually has?',
  'Novelty — is there something here they have not already read ten times?',
  'Specificity — are there real particulars, or only categories and adjectives?',
  'Credibility — is the claim believable, and is it supported by the evidence on file?',
];

/**
 * FAILURE MODES BEYOND THE HOOK — the judge's rubric only ever scored the
 * hook and genericness, so a headline or description that was individually
 * "fine" but broken as a whole shipped anyway. Two real defects seeded this
 * (2026-09-24, `ad_units` fd323b07/9155e7a5 on brief a30e05c0):
 *
 * 1. `description: "Holds in, valuations down"` — two noun fragments spliced
 *    by a comma with no verb tying them together. It reads as a stenographer's
 *    note, not a claim a reader can parse in one pass.
 * 2. `headline: "Stranded by ERCOT? Cut taxes on unpowered sites by 43%."` —
 *    the account's own brand-wide statistic (evidenced as "an average 43%
 *    first-year tax reduction across complex, capital-intensive portfolios
 *    nationwide") re-scoped to "unpowered sites", a scenario the evidence
 *    never measured. The figure is sourced; the SCENARIO it is attached to is
 *    not, and a reader cannot tell the difference from the sourced figure
 *    alone.
 *
 * This is the judge-rubric half of that fix — see
 * `generate-grounded-ad-copy.ts`'s `evaluateQualityDoctrine` for the
 * deterministic doctrine-rule half that runs on every candidate whether or
 * not the judge catches it.
 */
export const AD_COPY_ADDITIONAL_JUDGE_CHECKS: string[] = [
  'Coherent phrasing — every short field (a headline or a description) must read as ONE standalone phrase with a clear subject and a verb or a clear meaning on its own. A comma-spliced pair of fragments ("Holds in, valuations down") is a FAIL even when each half is individually true — name it in `failsBecause`.',
  'Scenario scope — a brand-wide statistic (one the evidence scopes with words like "average", "median", or "across ... portfolios") must never be re-scoped to a specific, unmeasured scenario the direction names (for example, tying a nationwide average percentage to "unpowered sites" when the evidence never measured that scenario). The number can be sourced and the claim still be false — flag this even when the figure itself checks out.',
];

/**
 * WHAT PAID COPY MAY CLAIM ABOUT WHO DID WHAT, WHAT THE BRAND ACHIEVES, AND
 * WHAT THE READER'S SITUATION IS. Each line is also a deterministic check
 * (`ad-copy-claims.ts`) or a judge check (`review-ad-copy.ts`), so a model
 * that ignores it is caught after writing, not trusted.
 */
export const PAID_CLAIMS_RULES: string[] = [
  'The brand\'s own result statistic (an average saving, a percentage improvement) is the BRAND\'s claim. State it once, in the body, attributed to the brand ("[Brand] reports" or "we report"), scoped exactly as the evidence scopes it, and caveated ("firm-wide average; results vary"). A result the evidence gives for ONE client (a results tile: an industry, a figure, what was saved, a place) is not an average: state it as the brand\'s report of that one client, in the evidence\'s own words, and add no "firm-wide" or "results vary" caveat to it, because the sentence already says whose result it is. Never in a description. A headline may lead with it only framed as that one client\'s result ("$10M property tax saved for a Texas energy client"), never as a promise to the reader. Never as "delivers", "gets you" or "you will", never twice.',
  'Say what an agency, a rule or a state did, never what a named official did. No governor, senator, mayor, candidate or election appears in paid copy.',
  "Never open on, or quote, the source article's headline. Open on one fact from its body, in a sentence of its own.",
  'Assert only what a line in the evidence states. A claim about how a tax, valuation, legal or regulatory mechanism works, or about what a rule causes, is an assertion the evidence must make; the fact that something is adjacent to your service is not evidence for it. Where the evidence states the fact but not the consequence, state the fact and stop.',
  'Never tell the reader what state their own assets, projects or plans are in ("your models are in limbo"). Name the situation the source describes and what it puts at stake for someone in their role.',
  'No adverb doing a claim\'s job ("aggressively", "always", "completely"). The specific thing the brand does is stronger.',
  'Keep every hedge the source carries, in the same sentence as the fact: "as few as 28%" is not "only 28%", "appears to address" is not "addresses". Say what an order told someone to do, not that it has been done ("directed the agency to penalize" is not "now penalizes"). A sentence that cannot keep its hedge is left out.',
  'ONE ask, and it is the offer. Name an offer only in words the brand\'s own text uses; never coin a deliverable ("a private review", "a findings memo", "a report") the brand text does not describe. If the brand text names no offer, invite the reader to contact the brand and promise nothing else. No request to comment, share or reply in a paid ad.',
];

/**
 * LinkedIn-specific craft.
 *
 * The register difference is real and structural, not cosmetic: the reader is
 * at work, mid-task, scrolling a feed of professional updates, and most of them
 * cannot buy today — LinkedIn's own B2B Institute popularised the finding that
 * roughly 95% of a B2B market is out-of-market at any moment. Copy written as
 * if everyone were ready to buy therefore fails against the majority of the
 * people who see it, which is why "be specific and useful" beats "convert now"
 * here in a way it does not on a Meta impulse purchase.
 */
export const LINKEDIN_CRAFT_RULES: string[] = [
  'Assume most readers cannot buy today. Roughly 95% of a business market is out-of-market at any moment, so copy that only makes sense to someone ready to purchase is wasted on nearly everyone who reads it. Be specific and genuinely useful enough to be remembered by the reader who will be in-market next quarter.',
  'Lead with the insight or the value, not the pitch. A concrete observation, a number, or a useful resource earns the read; an introduction to the company does not.',
  "Translate every feature into the reader's actual workday. Nobody cares that a product has forty integrations; they care that they stop rebuilding the same spreadsheet every Friday. Name the task that goes away.",
  'Vague authority is worse than none. "Trusted by thousands" earns nothing — a named result, a real figure, or a specific customer situation earns everything. If no such specific is on file, make the copy narrower rather than louder.',
  'Write peer-to-peer, the way a competent colleague explains something. No hype, no growth-hacker voice, no thought-leader posturing, no engagement bait ("Agree?", "Thoughts?"), no rocket emoji.',
  'Use industry vocabulary only where it is the accurate word. Reaching for jargon to sound credible reads as someone who does not do the job.',
  'The headline is a hard truncation with no expansion — what does not fit is gone for good. Make it a standalone statement of what this is or does, in one of these shapes: benefit for a named role, action verb plus outcome, a number plus a specific result, or how-to plus a tangible outcome.',
  'Do not repeat the CTA button\'s own label ("Learn more") in the copy. The commentary still closes on the ONE low-friction next step the evidence says the brand offers, written as a statement of what the brand offers ("X offers a ...") and never as an instruction to the reader ("Request a ...", "Book a ...", "Contact us"): the button carries the ask, and an imperative sentence in the intro is a second one. Never a promised outcome. If the evidence names no offer, ask only for what the reader can ask for and invent no terms.',
];

/**
 * THE COPY BRIEF — the method, restated as a brief a copywriter could work
 * from, rather than a list of things not to do.
 *
 * PROVENANCE OF THE REWRITE. On 2026-09-03 the pipeline was routed to the
 * frontier text model, the anti-corny gate was moved inside generation, and
 * a judge score below 7 started marking `needs_review`. The output for
 * Mora's own ad still graded 5/10 honestly: "The standard social media
 * workflow feels far too complicated. You need to review a clear plan
 * before production starts, but mapping out weeks of posts usually eats up
 * days of your time. Mora acts as an autonomous marketing partner that
 * generates voice-aligned social posts and schedules them to major channels
 * so you can approve four weeks of material before dinner."
 *
 * Every gate that copy passed was a gate. What was missing was a BRIEF. It
 * narrates the product instead of the reader; its hook is an abstraction
 * ("feels far too complicated") rather than a scene or a number; it stacks
 * three claims where an ad carries one; and it ignores the creative it runs
 * beside entirely. `METHOD-how-they-write-copy.md` names the fix in one
 * line — "the image already chose the lane; your copy writes the strongest
 * version inside it" — and the direction of causality it implies (creative
 * upstream of copy) is the change these rules make.
 *
 * Ordered so the reader-model hits the creative before it hits anything it
 * could write without looking at one.
 */
export const COPY_BRIEF_RULES: string[] = [
  'Open from the creative, which means open CONGRUENT with it — never by describing it. Naming an object in the frame ("that phone stays face down", "the van is already loaded") is a riddle the reader has to decode, and a cold reader will not, and writing the MOOD of the frame — the light, the weather, the pose, the fact that someone is resting — is worse: it is a caption on a picture the reader is already looking at, and the picture was paid for once already. This is not a reason to abandon the picture\'s vocabulary: its key NOUNS are still carried into the hook or a headline, exactly as the last rule in this brief requires. A noun the picture names is shared vocabulary; its light and its pose are not. The hook names a TASK the reader actually does, in the reader\'s own nouns and at its real frequency: the Friday reconciliation, the Sunday-night timesheet, the second trip across town. It must be true of the person in the picture without mentioning anything in the picture.',
  'One reader, one tension, one promise per candidate. Before writing, settle all three silently: who this single person is, the one thing that is true and uncomfortable about their week, and the one thing they get. Anything that does not serve those three is cut, however good the sentence is.',
  `The hook is the first sentence and it runs ${COPY_BRIEF_LIMITS.hookMaxWords} words or fewer. It must contain a concrete noun, a named moment, or a figure the next rule allows — a thing a person could photograph. An adjective-only abstraction ("too complicated", "overwhelming", "time-consuming") is not a hook; it is a category, and categories do not stop a scroll.`,
  'Exactly ONE claim, with the specific thing that makes it true, in plain words a person would say out loud. Plain words means a verb a person can picture. Do not force the word "because". A second claim is a second ad.',
  "EVERY NUMBER IN THE AD HAS A SOURCE. A count, a duration, a price, a percentage, a rating or a customer number that describes what the PRODUCT produces, promises or saves may appear ONLY when that exact figure is written in the evidence above. Where the evidence gives you no figure, write the mechanism WITHOUT one — name the input and the output and stop. A number that merely sounds right is the fastest thing in an ad for a reader to disprove, and it is the line a competitor screenshots. A number about the READER's own week — how often they do the task, how long their Saturday is — is not a product claim and is free, provided the reader would recognise it as their own.",
  `For cold prospecting, the whole primary text runs ${COPY_BRIEF_LIMITS.primaryTextMaxWords} words or fewer. The punchy shape runs ${COPY_BRIEF_LIMITS.punchyMaxWords} or fewer; the fuller shape runs ${COPY_BRIEF_LIMITS.fullerMinWords} to ${COPY_BRIEF_LIMITS.primaryTextMaxWords}. Count the words in each before you hand them back, and cut to fit rather than trusting the feel of it. Assume cold unless the direction says the reader already knows this brand. Never pad to reach a length.`,
  `The headline is ${COPY_BRIEF_LIMITS.headlineMaxWords} words or fewer and has to work read alone, with no primary text above it. It is not a summary of the primary text and must not repeat its first line.`,
  `The description is ${COPY_BRIEF_LIMITS.descriptionMaxChars} characters or fewer and is the last thing a reader is given, so it has to buy something. It may be a price, a free tier, a setup time, a count, a guarantee, or one real proof number — and only where that exact fact is written in the evidence above. Two shapes fail every single time and may never be written: a TARGETING LABEL ("For small clinics", "For busy owners"), which is an audience setting printed on the ad and buys nothing; and a BARE CATEGORY NAME ("Bookkeeping tools"). It may not repeat a phrase already used in the primary text or a headline, and it may not introduce a capability the ad has not already claimed — a second, unrelated feature in the last line is a different ad. When the evidence supports none of the six, compress the ad's ONE claim — the single promise this candidate is built on, never a secondary capability the body happened to mention in passing — into its shortest concrete form, in different words ("Receipts in, ledger out").`,
  'Write to the framework you were assigned, and let it decide the structure of the sentences. A framework is an argument shape, not a label to put over copy that would have been the same anyway.',
  'Say what the product does in verbs the reader can picture. Where the brand describes itself in marketing adjectives, translate: name the action and what it produces, in the order it happens.',
  'The swap-proof part of any product is its INPUT and its OUTPUT — the specific thing the reader already owns that goes in, and the specific thing that comes out. "Send the knives you already own on Tuesday and get them back ground to fifteen degrees" survives a competitor swapping their name in; "saves you hours" does not. Put the input and the output in the mechanism clause, in the reader\'s own nouns. Name the input as a THING the reader already owns ("the receipts in the shoebox"), never as an act the product performs on them ("it reads your books") — the second one is the product narrating itself, which is the failure mode directly above. The swap-proof differentiator belongs in this mechanism clause, in the primary text.',
  `A headline is a CLAUSE WITH A VERB, carrying a named object or a figure the evidence supports. A bare noun phrase ("Mobile grooming services", "Restaurant bookkeeping") is a label, not a headline, and it wastes the most-read line in the ad. Use the same product nouns as the primary text — that is what makes the ad one object — but never restate the hook as a sentence: the headline supplies the object, the moment, or the outcome the hook left out. The pain headline STATES the repeated task as a fact the reader recognises ("Her groom takes four hours."); the outcome headline names what comes out ("Groomed in your driveway by noon"). A yes/no question ("Still driving her across town?") is answered "no" for free and costs nothing to scroll past, so it is never the pain headline. Both run ${COPY_BRIEF_LIMITS.headlineMaxWords} words or fewer and are held to the same swap test as the body.`,
  'Carry the creative\'s own key nouns into the hook or a headline, so the picture and the copy read as one object — a reader must see the same words in both. Then add what the picture cannot show. If the image says "TWO HOURS OF PAPERWORK, GONE", the copy owes the reader WHICH paperwork and what happens to it, in that same vocabulary; restating the slogan on its own is a wasted sentence, and so is describing the scene the reader is already looking at.',
];

/**
 * A WORKED EXAMPLE PER FRAMEWORK, each written for a DIFFERENT and
 * deliberately unrelated brand.
 *
 * Why unrelated: an example written for the brand being advertised is an
 * answer, and a model handed an answer returns it. These are a dog-grooming
 * van, a restaurant bookkeeper, a knife sharpener, a dental payroll tool, a
 * used-instrument shop and a hood-cleaning company — close enough in SHAPE
 * to be copied as a shape, far enough in subject that copying the sentences
 * is impossible. Each one satisfies `COPY_BRIEF_RULES` exactly: hook under
 * 12 words carrying a concrete noun or number, one claim with its mechanism
 * in the same sentence, under 60 words total.
 */
export const HOOK_FRAMEWORK_WORKED_EXAMPLES: Record<HookFrameworkId, string> = {
  problem_agitate_solve:
    'Mobile dog grooming. "Her last groom meant four hours in a crate. Four hours of waiting, two trips across town, and a dog who shakes for the rest of the day. The van parks in your driveway and grooms one dog at a time, so she is done in forty-five minutes and never leaves the street."',
  before_after_bridge:
    'Bookkeeping for restaurants. "Friday used to end with a shoebox of receipts. Now it ends at nine. We pull your POS export every night and reconcile it against the bank before you close out, so the shoebox never fills up."',
  proof_lead:
    'Knife sharpening for kitchens. "312 Chicago line cooks ship us their knives every Tuesday. We grind to fifteen degrees on a water wheel instead of a belt, so the edge survives a full Saturday service instead of dying by seven."',
  identity_callout:
    'Payroll for dental practices. "You run a six-chair practice off one spreadsheet. Hygienist hours import straight from the appointment schedule, so nobody retypes a timesheet on a Sunday night."',
  objection_flip:
    'Used string instruments. "Buying a used cello online is a gamble. Every one of ours ships with the luthier setup sheet and thirty days to send it back, so the gamble is ours."',
  contrarian:
    'Commercial kitchen hood cleaning. "Quarterly is the wrong schedule for a wok line. Grease load tracks burner hours, not calendar months, so we set your interval from your own ticket count and clean before it becomes a fire code problem."',
};

/**
 * BRAND VOICE AS SENTENCE-LEVEL RULES.
 *
 * A brand kit stores voice as adjectives ("Pragmatic, Aesthetic,
 * Transparent, Productive"). Handing those adjectives to a model as
 * adjectives produces copy that TRIES to sound pragmatic, which is how
 * "Mora acts as an autonomous marketing partner" gets written by a model
 * that was told to be transparent. Each adjective is therefore rendered as
 * a rule about sentences — something checkable in the finished line, not a
 * mood to aim at.
 *
 * Unmapped adjectives fall through to a rule that is still sentence-level:
 * demonstrate it, never claim it, and never print the word.
 */
export const BRAND_VOICE_ADJECTIVE_RULES: Record<string, string> = {
  pragmatic:
    'Pragmatic: no superlatives, and no adjective doing the job of a fact. Every sentence names something that happens.',
  transparent:
    'Transparent: never overstate. Where a claim would only be true under a condition, NARROW THE CLAIM until it is true unconditionally — never append the condition as a disclaimer. A prerequisite ("it requires an existing website") is landing-page copy; in an ad it reads as a product apologising for itself.',
  honest:
    'Honest: never overstate. Where a claim would only be true under a condition, narrow the claim until it is true unconditionally, rather than appending the condition as a disclaimer.',
  authentic:
    'Authentic: write the sentence a person would actually say out loud. If it needs a comma to survive, it is written, not said.',
  aesthetic:
    'Aesthetic: the image carries the look. Never describe the visual style in words — no "beautiful", "stunning", "elevated".',
  productive:
    'Productive: name the time or the step that goes away, with its unit — minutes, one export, a Sunday night.',
  efficient:
    'Efficient: name the time or the step that goes away, with its unit — minutes, one export, a Sunday night.',
  friendly: 'Friendly: contractions, second person, and no exclamation marks.',
  warm: 'Warm: contractions, second person, and no exclamation marks.',
  bold: 'Bold: short declarative sentences, one strong verb each, and no hedging ("can help", "may", "designed to").',
  confident:
    'Confident: short declarative sentences, one strong verb each, and no hedging ("can help", "may", "designed to").',
  playful:
    'Playful: at most one piece of wit, and it belongs in the hook. The claim sentence stays straight.',
  fun: 'Fun: at most one piece of wit, and it belongs in the hook. The claim sentence stays straight.',
  premium:
    'Premium: no discount language. Specify material, method, or origin where an adjective would otherwise go.',
  luxurious:
    'Luxurious: no discount language. Specify material, method, or origin where an adjective would otherwise go.',
  professional:
    'Professional: no slang and no emoji. The reader is at work and is being interrupted.',
  expert:
    'Expert: name the method or the number. Never claim the expertise — "we grind to fifteen degrees", not "we are experts".',
  authoritative:
    'Authoritative: name the method or the number. Never claim the authority — "we grind to fifteen degrees", not "we are the leader".',
  minimal: 'Minimal: one clause per sentence, and every qualifier cut.',
  simple: 'Simple: one clause per sentence, and every qualifier cut.',
  clear: 'Clear: one clause per sentence, and every qualifier cut.',
  energetic:
    'Energetic: lead with verbs. At most one exclamation mark in the whole ad, and never two in a row.',
  empathetic:
    'Empathetic: name the reader\'s situation before you name the product, and never diagnose the reader ("you are overwhelmed").',
  caring:
    'Caring: name the reader\'s situation before you name the product, and never diagnose the reader ("you are overwhelmed").',
  innovative:
    'Innovative: describe the mechanism that is actually new. The word "new" on its own is not a mechanism.',
  approachable:
    "Approachable: 7th-grade words, and no jargon that is not already the reader's own word for the thing.",
  technical:
    'Technical: use the precise term where it is the accurate one, and define nothing the reader already knows.',
  sustainable: 'Sustainable: state the material or the process. Never state the virtue.',
  bespoke: 'Bespoke: name what changes per customer, specifically, or drop the idea.',
};

/**
 * Splits a brand kit's stored tone string into its adjectives and renders
 * each as a sentence-level rule. Adjectives with no mapping still get a
 * rule, because "be witty" is unusable and "demonstrate it, never print the
 * word" is not.
 */
export function renderVoiceRules(tone: string | null | undefined): string[] {
  const raw = (tone ?? '').trim();
  if (!raw) return [];
  const adjectives = raw
    .split(/[,/|·•]|\band\b/i)
    .map((part) => part.trim().replace(/[.]+$/, ''))
    .filter((part) => part.length > 0 && part.length < 40)
    .slice(0, 8);
  if (adjectives.length === 0) return [];
  return adjectives.map((adjective) => {
    const mapped = BRAND_VOICE_ADJECTIVE_RULES[adjective.toLowerCase()];
    if (mapped) return `- ${mapped}`;
    const label = adjective.charAt(0).toUpperCase() + adjective.slice(1);
    return `- ${label}: make one sentence demonstrably ${adjective.toLowerCase()} by what it says. The word "${adjective.toLowerCase()}" itself may never appear in the copy.`;
  });
}

/**
 * THE SWAP TEST, STATED POSITIVELY.
 *
 * `voc-to-angles` runs it as a rejection ("put a competitor's name on it; if
 * it still reads true, it is generic"), and `reviewAdCopy` already asks the
 * judge that question after the fact. Asked of a model BEFORE it writes, a
 * rejection produces hedging; an instruction to go and find the one thing
 * only this brand does produces a sentence. These lines are the second
 * form.
 *
 * The claim shapes below are the ones every advertiser in every category can
 * make truthfully, which is exactly what makes them worthless as the single
 * claim of an ad.
 */
export const CATEGORY_GENERIC_CLAIMS: string[] = [
  'saves you time',
  'easy to use',
  'everything in one place',
  'built for people like you',
  'high quality',
  'trusted by thousands',
  'the smart way to do it',
  'so you can focus on what matters',
  'takes the hassle out of it',
  'works the way you do',
  'reclaim your weekends',
  'get your weekend back',
  'hours back in your week',
  'stop the Sunday scramble',
  'work from anywhere',
  'content that converts',
  'never run out of ideas',
  'so you can finally relax',
];

export function buildSwapTestBlock(brand: BrandGround): string[] {
  const competitors = truncateList(brand.competitors, 6);
  const lines: string[] = [
    'SWAP TEST — do this BEFORE you write, not after:',
    competitors.length
      ? `1. Name silently three sentences ${competitors.join(', ')} could each say about their own product, just as truthfully. Those sentences are now spent — none of them may appear in this ad in any wording.`
      : '1. Name silently three sentences a direct competitor could say about their own product, just as truthfully. Those sentences are now spent — none of them may appear in this ad in any wording.',
    `2. These claim shapes are truthful for every advertiser alive, so none of them may appear as this ad's claim, headline, or description in any wording: ${CATEGORY_GENERIC_CLAIMS.join('; ')}.`,
    "3. Now find the one thing in the evidence above that this brand does and a competitor could not say — usually a SEQUENCE, an order of operations, a specific input it reads, or a specific thing it produces. Name that input and that output in the reader's own nouns, and put them in the mechanism clause and in the headline. If the evidence supports no such thing, write a narrower ad about the one specific thing it does support rather than a broader ad about the category.",
  ];
  return lines;
}

/**
 * Evidence lines are facts. They are not phrasing, and the distinction is
 * load-bearing here: the citable evidence for Mora's own kit literally
 * contains "an AI-powered social media manager" and "acts as an autonomous
 * marketing partner", so a model correctly told to cite verbatim will
 * reproduce house marketing language it is separately forbidden to use.
 * That is not a model failure — it is an instruction that never existed.
 */
export const EVIDENCE_IS_FACT_NOT_PHRASING =
  'Evidence lines give you FACTS, not phrasing. Take the fact and say it in the reader\'s own plain words. A brand describing itself as intelligent, seamless, powerful, effortless, AI-powered or an autonomous partner is the brand talking to itself; those words stay out of the ad even when they appear verbatim in an evidence line above. Keep verbatim quoting for numbers, product names, prices and real customer quotes. A page fragment is cut from a longer sentence and often does not parse alone ("by pursuing valuations with how your buildings operate"): restate the fact in a sentence of your own that is complete English.';

interface BannedProductLanguageTerm {
  readonly label: string;
  readonly pattern: RegExp;
  /** Stated as the next move, never as a ban — negative prompts are a no-op on this repo's generation models. */
  readonly fix: string;
}

/**
 * BANNED PRODUCT LANGUAGE, APPLIED TO OUTPUT.
 *
 * CLAUDE.md's product-language rule is written about Mora's own surfaces,
 * and the six terms below carry the same failure into an ad: each of them
 * claims a capability the copy has not shown. They are matched against
 * every field that ships.
 *
 * WHY "launch" AND "live" ARE NARROW HERE, and not blanket bans like the
 * rest. In Mora's UI those two words are banned outright, because "launch"
 * must not mean "saved a draft" and "live" must not mean "PAUSED". In an
 * ADVERTISEMENT they are ordinary English owned by the advertiser — a bakery
 * launches a winter menu, a band plays live — and a blanket ban would reject
 * correct copy for every brand that is not a marketing tool. The pattern
 * therefore fires only where the ADVERTISED PRODUCT is the thing doing the
 * launching or going live on its own, which is the overclaim the rule exists
 * to stop.
 */
/**
 * "GUARANTEE" IS A WORD AN ADVERTISER OWNS; "GUARANTEED RESULTS" IS AN
 * OVERCLAIM. The two are not the same term and must not share one pattern.
 *
 * The first version of this check banned the whole word with two literal
 * exemptions ("money-back", "satisfaction"), which flagged `30-day
 * guarantee` and `Free returns, guarantee included` — the single most
 * valuable thing a real advertiser can put in a 30-character description,
 * and a thing `COPY_BRIEF_RULES` explicitly PERMITS there. A gate that
 * contradicts the brief burns real regenerations and then ships the copy
 * carrying a false defect, so the term is narrowed to the shape that is
 * actually an overclaim: a guaranteed OUTCOME (results, growth, revenue,
 * attribution, rankings), or the product/advertiser guaranteeing something
 * as a verb. An offer-shaped guarantee — a duration, a refund, a return
 * window, satisfaction — is left alone, because it is a term of sale the
 * advertiser can actually honour.
 */
const GUARANTEED_OUTCOME_NOUNS =
  'results?|growth|sales|revenue|roi|returns on|conversions?|leads?|traffic|rankings?|placements?|success|profits?|attribution|followers?|views?|impressions?|engagement|reach|approvals?|savings?';

const GUARANTEED_OUTCOME_PATTERN = new RegExp(
  [
    `\\bguaranteed\\s+(?:\\w+\\s+){0,2}(?:${GUARANTEED_OUTCOME_NOUNS})\\b`,
    `\\b(?:${GUARANTEED_OUTCOME_NOUNS})[\\s,]+(?:\\w+[\\s,]+){0,2}guaranteed\\b`,
    '\\bguaranteed\\s+to\\s+\\w+',
    '\\b(?:we|it|this|they|the\\s+(?:app|tool|agent|platform|system|software))\\s+guarantees?\\b',
  ].join('|'),
  'i',
);

const BANNED_PRODUCT_LANGUAGE_TERMS: readonly BannedProductLanguageTerm[] = [
  {
    label: 'magic',
    pattern: /\bmagic(?:al|ally)?\b/i,
    fix: 'Replace the word "magic" with the specific step that produces the result, in one plain verb.',
  },
  {
    label: 'autopilot',
    pattern: /\bauto[- ]?pilot\b/i,
    fix: 'Replace "autopilot" with what the reader still decides and what the product does between their decisions.',
  },
  {
    label: 'autonomous',
    pattern: /\bautonomous(?:ly)?\b/i,
    fix: 'Replace "autonomous" with the specific action the product takes and the point where the reader approves it.',
  },
  {
    label: 'AI-powered',
    pattern: /\b(?:AI[- ]powered|powered by AI|AI[- ]driven)\b/i,
    fix: 'Replace "AI-powered" with what the product produces for the reader; how it is built is not a benefit.',
  },
  {
    label: 'guaranteed outcome',
    pattern: GUARANTEED_OUTCOME_PATTERN,
    fix: 'Replace the guaranteed outcome with the terms that are actually guaranteed — the refund window, the return policy, the delivery date — or cut the promise.',
  },
  {
    label: 'winner',
    pattern: /\bwinn(?:er|ers|ing)\b/i,
    fix: 'Replace "winner"/"winning" with the comparison that was actually made and the number behind it, or cut the claim.',
  },
  {
    label: 'launch (attributed to the product)',
    pattern:
      /\b(?:it|this|the (?:app|tool|agent|platform|system|software))\s+(?:\w+\s+){0,3}launch(?:es|ed|ing)?\b|launch(?:es|ed|ing)?\s+(?:them|it|your \w+)\s+(?:for you|automatically|on its own)\b/i,
    fix: 'Say what the product prepares and where the reader presses the button, rather than saying the product launches anything.',
  },
  {
    label: 'live (attributed to the product)',
    pattern: /\b(?:goes|go|going)\s+live\s+(?:automatically|for you|on its own|by itself)\b/i,
    fix: 'Say what state the work is left in and who publishes it, rather than saying it goes live on its own.',
  },
];

export interface BannedProductLanguageHit {
  readonly field: string;
  readonly term: string;
  readonly match: string;
  readonly fix: string;
}

/**
 * Returns every banned-language hit across the fields given. Pure, so it can
 * run inside generation as a countable gate rather than as advice a model
 * may sound confident past.
 */
export function findBannedProductLanguage(
  fields: readonly { field: string; text: string | null | undefined }[],
): BannedProductLanguageHit[] {
  const hits: BannedProductLanguageHit[] = [];
  for (const { field, text } of fields) {
    const value = (text ?? '').trim();
    if (!value) continue;
    for (const term of BANNED_PRODUCT_LANGUAGE_TERMS) {
      const match = term.pattern.exec(value);
      if (!match) continue;
      hits.push({ field, term: term.label, match: match[0], fix: term.fix });
    }
  }
  return hits;
}
